import { useEffect, useMemo, useState } from 'react';
import { DecimalInput } from '../ui/DecimalInput';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import {
  AGGREGATE_LABEL,
  aggregateOptions,
  fieldUnit,
  isNumeric,
  sameMeasure,
  DASH_DAYS,
  DASHBOARD_TEMPLATES,
  defaultConfig,
  DUE_FILTER_LABEL,
  DUE_FILTERS,
  FIELD_OP_TEXT,
  FIELD_OPS_BY_KIND,
  fieldKind,
  type CustomField,
  type FieldCond,
  type FieldOp,
  type LabelColor,
  MAX_WIDGETS,
  PRIORITIES,
  ROW_CHOICES,
  safeLink,
  WIDGET_INFO,
  WIDGET_TYPES,
  CAL_COLOR_TEXT,
  CAL_COLORS,
  CAL_DATES,
  CAL_DATES_TEXT,
  CAL_RANGE_TEXT,
  CAL_RANGES,
  CAL_SHOW,
  CAL_SHOW_TEXT,
  DIST_BY,
  DIST_BY_TEXT,
  type DashBoards,
  type DashboardTemplate,
  type DashFilter,
  type DueFilter,
  type Widget,
  type WidgetSize,
  type WidgetType,
} from '@kaba/shared';
import { useBoards } from '../../lib/board';
import { api } from '../../lib/api';
import { useNav } from '../../lib/structure';
import { inputCls } from '../AdminPage';
import { Button, Sheet, cx } from '../ui/ui';
import type { DashCtx } from './DashboardView';

const PERSONAL: DashCtx = { scope: 'personal', teamId: null };

export const SIZE_TEXT: Record<WidgetSize, string> = { 4: 'Schmal (ein Drittel)', 6: 'Halb', 12: 'Ganze Breite' };

/** Zufällige, stabile Baustein-ID (auch ohne https) */
export function newWidgetId(): string {
  const b = new Uint8Array(9);
  crypto.getRandomValues(b);
  return btoa(String.fromCharCode(...b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

const fieldset = 'flex flex-col gap-1.5';
const legend = 'mb-1 text-sm font-semibold';
const check = 'size-5 accent-[var(--color-accent)]';
const row = 'flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9';

/** Boardauswahl: alle, feste Auswahl oder dynamisch ein Bereich (gewährt keine Rechte) */
function BoardsField({ value, onChange, id, ctx = PERSONAL }: { value: DashBoards; onChange: (v: DashBoards) => void; id: string; ctx?: DashCtx }) {
  const { data: boards } = useBoards();
  const { data: navAll } = useNav();
  // Teamübersicht: nur Bereiche dieses Teams
  const nav = navAll && ctx.teamId ? { ...navAll, areas: navAll.areas.filter((a) => a.teamId === ctx.teamId) } : navAll;
  const active = (boards ?? []).filter((b) => !b.archivedAt);
  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-1 text-sm" htmlFor={`${id}-mode`}>
        <span className="font-semibold">Boards</span>
        <select
          id={`${id}-mode`}
          className={inputCls}
          value={value.mode}
          onChange={(e) => {
            const m = e.target.value as DashBoards['mode'];
            onChange(
              m === 'all'
                ? { mode: 'all' }
                : m === 'team' && ctx.teamId
                  ? { mode: 'team', teamId: ctx.teamId }
                  : m === 'selected'
                    ? { mode: 'selected', ids: [] }
                    : { mode: 'area', areaId: nav?.areas[0]?.id ?? '' },
            );
          }}
        >
          {ctx.scope === 'team' && <option value="team">Alle Boards des Teams (wird jedes Mal neu bestimmt)</option>}
          <option value="all">{ctx.scope === 'team' ? 'Alle Boards, auf die die jeweilige Person Zugriff hat' : 'Alle Boards, auf die ich Zugriff habe'}</option>
          <option value="selected">Ausgewählte Boards</option>
          {!!nav?.areas.length && <option value="area">Alle Boards eines Bereichs (wird jedes Mal neu bestimmt)</option>}
        </select>
      </label>
      {value.mode === 'selected' && (
        <fieldset className="max-h-56 overflow-y-auto rounded-lg border border-[var(--line)] p-2">
          <legend className="sr-only">Boards auswählen</legend>
          {active.map((b) => (
            <label key={b.id} className={row}>
              <input
                type="checkbox"
                className={check}
                checked={value.ids.includes(b.id)}
                onChange={(e) => onChange({ mode: 'selected', ids: e.target.checked ? [...value.ids, b.id] : value.ids.filter((x) => x !== b.id) })}
              />
              <span className="break-words">{b.title}</span>
            </label>
          ))}
          {value.ids.some((x) => !active.some((b) => b.id === x)) && (
            <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">Mindestens ein ausgewähltes Board ist nicht mehr verfügbar und wird nicht berücksichtigt.</p>
          )}
        </fieldset>
      )}
      {value.mode === 'area' && (
        <select aria-label="Bereich" className={inputCls} value={value.areaId} onChange={(e) => onChange({ mode: 'area', areaId: e.target.value })}>
          {!nav?.areas.some((a) => a.id === value.areaId) && <option value={value.areaId}>Nicht mehr verfügbarer Bereich</option>}
          {nav?.areas.map((a) => (
            <option key={a.id} value={a.id}>
              {a.teamName} / {a.name}
            </option>
          ))}
        </select>
      )}
      <p className="text-xs text-[var(--ink-soft)]">Bereiche ordnen nur. Gezählt werden immer nur Boards, auf die du selbst Zugriff hast.</p>
    </div>
  );
}

const WHO: [string, string][] = [
  ['', 'Alle'],
  ['me', 'Mir zugewiesen'],
  ['none', 'Ohne Zuständige'],
];

/** Ein Filterwert der Übersicht (auch als Abweichung am Baustein) */
export function FilterField({ k, f, set, id, ctx = PERSONAL }: { k: keyof DashFilter; f: DashFilter; set: (p: Partial<DashFilter>) => void; id: string; ctx?: DashCtx }) {
  if (k === 'boards') return ctx.scope === 'board' ? null : <BoardsField id={id} ctx={ctx} value={f.boards} onChange={(boards) => set({ boards })} />;
  if (k === 'assignees') return <PeopleField id={id} value={f.assignees} onChange={(assignees) => set({ assignees })} />;
  if (k === 'fields') return <FieldConds id={id} f={f} ctx={ctx} onChange={(fields) => set({ fields })} />;
  if (k === 'priority')
    return (
      <fieldset className={fieldset}>
        <legend className={legend}>Priorität (leer = jede)</legend>
        <div className="flex flex-wrap gap-x-4">
          {PRIORITIES.map((p) => (
            <label key={p.value} className={row}>
              <input type="checkbox" className={check} checked={f.priority.includes(p.value)} onChange={(e) => set({ priority: e.target.checked ? [...f.priority, p.value] : f.priority.filter((x) => x !== p.value) })} />
              {p.label}
            </label>
          ))}
        </div>
      </fieldset>
    );
  if (k === 'due')
    return (
      <fieldset className={fieldset}>
        <legend className={legend}>Frist (leer = jede)</legend>
        <div className="flex flex-wrap gap-x-4">
          {DUE_FILTERS.map((d: DueFilter) => (
            <label key={d} className={row}>
              <input type="checkbox" className={check} checked={f.due.includes(d)} onChange={(e) => set({ due: e.target.checked ? [...f.due, d] : f.due.filter((x) => x !== d) })} />
              {DUE_FILTER_LABEL[d]}
            </label>
          ))}
        </div>
      </fieldset>
    );
  return (
    <label className="flex flex-col gap-1 text-sm" htmlFor={`${id}-days`}>
      <span className="font-semibold">Zeitraum für Kennzahlen, Zeiten und Aktivität</span>
      <select id={`${id}-days`} className={inputCls} value={f.days} onChange={(e) => set({ days: Number(e.target.value) })}>
        {DASH_DAYS.map((d) => (
          <option key={d} value={d}>
            {d === 365 ? 'Letztes Jahr' : `Letzte ${d} Tage`}
          </option>
        ))}
      </select>
    </label>
  );
}

const FILTER_KEYS: (keyof DashFilter)[] = ['boards', 'assignees', 'priority', 'due', 'fields', 'days'];

/** Gemeinsame Filter der Übersicht */
export function DashFilterSheet({ open, filter, onClose, onApply, ctx = PERSONAL }: { open: boolean; filter: DashFilter; onClose: () => void; onApply: (f: DashFilter) => void; ctx?: DashCtx }) {
  const [f, setF] = useState(filter);
  useEffect(() => {
    if (open) setF(filter);
  }, [open, filter]);
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Filter der Übersicht" side="center">
      <div className="flex flex-col gap-4 p-4">
        <p className="text-sm text-[var(--ink-soft)]">Gelten für alle Bausteine, die nichts Abweichendes festlegen.</p>
        {FILTER_KEYS.filter((k) => ctx.scope !== 'board' || k !== 'boards').map((k) => (
          <FilterField key={k} k={k} f={f} ctx={ctx} id={`df-${k}`} set={(p) => setF({ ...f, ...p })} />
        ))}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
            disabled={f.boards.mode === 'selected' && !f.boards.ids.length}
            onClick={() => {
              onApply(f);
              onClose();
            }}
          >
            Übernehmen
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

/** Einstellungen eines Bausteins: Titel, Größe, Inhalt, abweichende Filter */
export function WidgetSettings({
  open,
  widget,
  dashFilter,
  onClose,
  onApply,
  ctx = PERSONAL,
}: {
  open: boolean;
  widget: Widget | null;
  dashFilter: DashFilter;
  onClose: () => void;
  onApply: (w: Widget) => void;
  ctx?: DashCtx;
}) {
  const [w, setW] = useState<Widget | null>(widget);
  useEffect(() => {
    if (open) setW(widget);
  }, [open, widget]);
  if (!w) return null;
  const info = WIDGET_INFO[w.type];
  const uses = info.uses.filter((k) => ctx.scope !== 'board' || k !== 'boards');
  const cfg = w.config as Record<string, unknown>;
  const setCfg = (p: Record<string, unknown>) => setW({ ...w, config: { ...cfg, ...p } } as Widget);
  const eff = { ...dashFilter, ...w.filter };
  const links = w.type === 'links' ? w.config.items : [];
  const badLink = links.some((l) => !l.title.trim() || !safeLink(l.url));
  const badBoards = (w.type === 'boards' && w.config.source === 'selected' && !w.config.boardIds.length) || (w.type === 'metric' && !w.config.map.length);
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title={`Baustein „${info.title}“`} side="center">
      <div className="flex flex-col gap-4 p-4">
        <label className="flex flex-col gap-1 text-sm" htmlFor="ws-title">
          <span className="font-semibold">Titel</span>
          <input id="ws-title" className={inputCls} maxLength={80} placeholder={info.title} value={w.title ?? ''} onChange={(e) => setW({ ...w, title: e.target.value || null })} />
        </label>
        <label className="flex flex-col gap-1 text-sm" htmlFor="ws-size">
          <span className="font-semibold">Breite am großen Bildschirm</span>
          <select id="ws-size" className={inputCls} value={w.size} onChange={(e) => setW({ ...w, size: Number(e.target.value) as WidgetSize })}>
            {([4, 6, 12] as WidgetSize[]).map((s) => (
              <option key={s} value={s}>
                {SIZE_TEXT[s]}
              </option>
            ))}
          </select>
        </label>
        {'rows' in cfg && (
          <label className="flex flex-col gap-1 text-sm" htmlFor="ws-rows">
            <span className="font-semibold">Sichtbare Zeilen</span>
            <select id="ws-rows" className={inputCls} value={cfg.rows as number} onChange={(e) => setCfg({ rows: Number(e.target.value) })}>
              {ROW_CHOICES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        )}
        {w.type === 'tasks' && (
          <label className="flex flex-col gap-1 text-sm" htmlFor="ws-sort">
            <span className="font-semibold">Sortierung</span>
            <select id="ws-sort" className={inputCls} value={w.config.sort} onChange={(e) => setCfg({ sort: e.target.value })}>
              <option value="due">Nach Frist, dann Priorität</option>
              <option value="priority">Nach Priorität, dann Frist</option>
              <option value="updated">Zuletzt geändert</option>
            </select>
          </label>
        )}
        {w.type === 'agenda' && (
          <label className="flex flex-col gap-1 text-sm" htmlFor="ws-adays">
            <span className="font-semibold">Vorschau</span>
            <select id="ws-adays" className={inputCls} value={w.config.days} onChange={(e) => setCfg({ days: Number(e.target.value) })}>
              {[7, 14, 30].map((d) => (
                <option key={d} value={d}>
                  {d} Tage
                </option>
              ))}
            </select>
          </label>
        )}
        {w.type === 'boards' && (
          <>
            <label className="flex flex-col gap-1 text-sm" htmlFor="ws-src">
              <span className="font-semibold">Welche Boards</span>
              <select id="ws-src" className={inputCls} value={w.config.source} onChange={(e) => setCfg({ source: e.target.value })}>
                <option value="favorites">Favoriten</option>
                <option value="recent">Zuletzt geöffnet (auf diesem Gerät)</option>
                <option value="selected">Ausgewählte Boards</option>
              </select>
            </label>
            {w.config.source === 'selected' && (
              <BoardsField id="ws-b" value={{ mode: 'selected', ids: w.config.boardIds }} onChange={(v) => setCfg({ boardIds: v.mode === 'selected' ? v.ids.slice(0, 50) : [] })} />
            )}
          </>
        )}
        {w.type === 'calendar' && <CalendarSettings cfg={w.config} onChange={(c) => setCfg(c)} />}
        {w.type === 'distribution' && (
          <>
            <Select id="ws-by" label="Aufteilen nach" value={w.config.by} onChange={(by) => setCfg({ by })} options={DIST_BY.map((k) => [k, DIST_BY_TEXT[k]])} />
            <Choice
              label="Darstellung"
              name="ws-chart"
              value={w.config.chart}
              onChange={(chart) => setCfg({ chart })}
              options={[
                ['bars', 'Balken'],
                ['donut', 'Ring mit Legende'],
              ]}
            />
            <Select
              id="ws-cards"
              label="Welche Karten"
              value={w.config.cards}
              onChange={(cards) => setCfg({ cards })}
              options={[
                ['open', 'Nur offene'],
                ['all', 'Alle (auch erledigte, ohne Archiv)'],
              ]}
            />
          </>
        )}
        {(w.type === 'trend' || w.type === 'punctuality') && (
          <Select
            id="ws-int"
            label="Abschnitte"
            value={w.config.interval}
            onChange={(interval) => setCfg({ interval })}
            options={[
              ['week', 'Wochen'],
              ['month', 'Monate'],
            ]}
          />
        )}
        {w.type === 'trend' && (
          <>
            <label className={row}>
              <input type="checkbox" className={check} checked={w.config.stock} onChange={(e) => setCfg({ stock: e.target.checked })} />
              Offenen Bestand als Verlauf zeigen
            </label>
            <p className="text-xs text-[var(--ink-soft)]">Wie weit zurück, bestimmt der Zeitraum (Filter).</p>
          </>
        )}
        {w.type === 'punctuality' && (
          <Select
            id="ws-grp"
            label="Zusätzlich aufteilen"
            value={w.config.groupBy}
            onChange={(groupBy) => setCfg({ groupBy })}
            options={[
              ['none', 'Nicht aufteilen'],
              ['assignee', 'Nach zuständiger Person'],
              ['board', 'Nach Board'],
            ]}
          />
        )}
        {w.type === 'workload' && (
          <>
            <Choice
              label="Maß"
              name="ws-measure"
              value={w.config.measure}
              onChange={(measure) => setCfg({ measure })}
              options={[
                ['cards', 'Anzahl offener Karten'],
                ['estimate', 'Geschätzter Aufwand'],
              ]}
            />
            <label className={row}>
              <input type="checkbox" className={check} checked={w.config.unassigned} onChange={(e) => setCfg({ unassigned: e.target.checked })} />
              Karten ohne Zuständige als eigene Zeile
            </label>
          </>
        )}
        {w.type === 'timeStats' && (
          <>
            <Choice
              label="Wessen Zeit"
              name="ws-people"
              value={w.config.people}
              onChange={(people) => setCfg({ people })}
              options={[
                ['me', 'Nur meine'],
                ['all', 'Alle Personen (in Boards, die ich sehe)'],
              ]}
            />
            <Select
              id="ws-tg"
              label="Aufteilen nach"
              value={w.config.groupBy}
              onChange={(groupBy) => setCfg({ groupBy })}
              options={[
                ['week', 'Woche'],
                ['board', 'Board'],
                ['person', 'Person'],
                ['card', 'Karte'],
              ]}
            />
          </>
        )}
        {w.type === 'metric' && <MetricSettings id="ws-m" cfg={w.config} filter={eff} onChange={(c) => setCfg(c)} />}
        {w.type === 'links' && (
          <fieldset className={fieldset}>
            <legend className={legend}>Verknüpfungen</legend>
            <p className="text-xs text-[var(--ink-soft)]">Seiten in KaBa (Adresse ab „/“, z. B. „/b/…“) oder sichere Adressen mit https://. Keine eingebetteten Seiten.</p>
            {links.map((l, i) => {
              const bad = !!l.url && !safeLink(l.url);
              return (
                <div key={i} className="flex flex-wrap items-start gap-2 rounded-lg border border-[var(--line)] p-2">
                  <input aria-label={`Titel ${i + 1}`} className={cx(inputCls, 'min-w-40 flex-1')} placeholder="Titel" maxLength={80} value={l.title} onChange={(e) => setCfg({ items: links.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })} />
                  <input
                    aria-label={`Adresse ${i + 1}`}
                    aria-invalid={bad || undefined}
                    className={cx(inputCls, 'min-w-48 flex-[2]')}
                    placeholder="https://… oder /b/…"
                    maxLength={500}
                    value={l.url}
                    onChange={(e) => setCfg({ items: links.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) })}
                  />
                  <Button variant="ghost" aria-label={`Verknüpfung ${i + 1} entfernen`} onClick={() => setCfg({ items: links.filter((_, j) => j !== i) })}>
                    <Trash2 size={15} aria-hidden />
                  </Button>
                  {bad && (
                    <p className="w-full text-xs text-rose-700 dark:text-rose-300" role="alert">
                      Nur Adressen in KaBa (/…) oder mit https://
                    </p>
                  )}
                </div>
              );
            })}
            {links.length < 12 && (
              <Button className="self-start" onClick={() => setCfg({ items: [...links, { title: '', url: '' }] })}>
                <Plus size={15} aria-hidden /> Verknüpfung
              </Button>
            )}
          </fieldset>
        )}
        {uses.length > 0 && (
          <fieldset className="flex flex-col gap-3 border-t border-[var(--line)] pt-3">
            <legend className="sr-only">Filter</legend>
            <p className="text-sm font-semibold">Filter</p>
            {uses.map((k) => {
              const own = k in w.filter;
              return (
                <div key={k} className="flex flex-col gap-2 rounded-lg bg-[var(--column)] p-2">
                  <label className={row}>
                    <input
                      type="checkbox"
                      className={check}
                      checked={own}
                      onChange={(e) => {
                        const next = { ...w.filter };
                        if (e.target.checked) (next as Record<string, unknown>)[k] = dashFilter[k];
                        else delete next[k];
                        setW({ ...w, filter: next });
                      }}
                    />
                    Abweichend von der Übersicht: {({ boards: 'Boards', assignees: 'Zuständig', priority: 'Priorität', due: 'Frist', days: 'Zeitraum', fields: 'Eigene Felder' } as const)[k]}
                  </label>
                  {own && <FilterField k={k} f={eff} ctx={ctx} id={`wf-${k}`} set={(p) => setW({ ...w, filter: { ...w.filter, ...p } })} />}
                </div>
              );
            })}
          </fieldset>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
            disabled={badLink || badBoards}
            onClick={() => {
              onApply(w);
              onClose();
            }}
          >
            Übernehmen
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

function Select<T extends string>({ id, label, value, onChange, options }: { id: string; label: string; value: T; onChange: (v: T) => void; options: [T, string][] }) {
  return (
    <label className="flex flex-col gap-1 text-sm" htmlFor={id}>
      <span className="font-semibold">{label}</span>
      <select id={id} className={inputCls} value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map(([k, t]) => (
          <option key={k} value={k}>
            {t}
          </option>
        ))}
      </select>
    </label>
  );
}

function Choice<T extends string>({ label, name, value, onChange, options }: { label: string; name: string; value: T; onChange: (v: T) => void; options: [T, string, string?][] }) {
  return (
    <fieldset className={fieldset}>
      <legend className={legend}>{label}</legend>
      {options.map(([k, t, hint]) => (
        <label key={k} className={cx(row, 'items-start')}>
          <input type="radio" name={name} className={cx(check, 'mt-0.5 shrink-0')} checked={value === k} onChange={() => onChange(k)} />
          <span>
            {t}
            {hint && <span className="block text-xs text-[var(--ink-soft)]">{hint}</span>}
          </span>
        </label>
      ))}
    </fieldset>
  );
}

type CalConfig = Extract<Widget, { type: 'calendar' }>['config'];

/** 0.87: Einstellungen des Kalenderbausteins */
function CalendarSettings({ cfg, onChange }: { cfg: CalConfig; onChange: (c: Partial<CalConfig>) => void }) {
  const hours = Array.from({ length: 25 }, (_, i) => i);
  const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;
  return (
    <>
      <Select id="ws-range" label="Zeitraum" value={cfg.range} onChange={(range) => onChange({ range })} options={CAL_RANGES.map((k) => [k, CAL_RANGE_TEXT[k]])} />
      <Select id="ws-dates" label="Welche Termine" value={cfg.dates} onChange={(dates) => onChange({ dates })} options={CAL_DATES.map((k) => [k, CAL_DATES_TEXT[k]])} />
      <Choice
        label="Darstellung"
        name="ws-look"
        value={cfg.look}
        onChange={(look) => onChange({ look })}
        options={[
          ['strips', 'Streifen', 'Eine Zeile je Aufgabe mit Titel – kompakt, viel auf einen Blick.'],
          ['blocks', 'Flächen', 'Woche/Arbeitswoche: Zeitraster mit genauer Uhrzeit, Dauer von Start bis Frist als Fläche. Zwei Wochen/Monat: größere Kacheln mit allen gewählten Angaben.'],
        ]}
      />
      <fieldset className={fieldset}>
        <legend className={legend}>Angaben am Eintrag</legend>
        <div className="flex flex-wrap gap-x-4">
          {CAL_SHOW.map((k) => (
            <label key={k} className={row}>
              <input
                type="checkbox"
                className={check}
                checked={cfg.show.includes(k)}
                onChange={(e) => onChange({ show: e.target.checked ? CAL_SHOW.filter((x) => x === k || cfg.show.includes(x)) : cfg.show.filter((x) => x !== k) })}
              />
              {CAL_SHOW_TEXT[k]}
            </label>
          ))}
        </div>
      </fieldset>
      <Select id="ws-color" label="Farbe" value={cfg.color} onChange={(color) => onChange({ color })} options={CAL_COLORS.map((k) => [k, CAL_COLOR_TEXT[k]])} />
      <label className={row}>
        <input type="checkbox" className={check} checked={cfg.done} onChange={(e) => onChange({ done: e.target.checked })} />
        Erledigte Karten zeigen (durchgestrichen)
      </label>
      {cfg.look === 'blocks' && (cfg.range === 'week' || cfg.range === 'workweek') && (
        <fieldset className={fieldset}>
          <legend className={legend}>Zeitraster</legend>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <label className="flex items-center gap-1.5">
              von
              <select aria-label="Zeitraster von" className={inputCls} value={cfg.hours[0]} onChange={(e) => onChange({ hours: [Number(e.target.value), Math.max(cfg.hours[1], Number(e.target.value) + 4)] })}>
                {hours.slice(0, 21).map((h) => (
                  <option key={h} value={h}>
                    {hh(h)}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1.5">
              bis
              <select aria-label="Zeitraster bis" className={inputCls} value={cfg.hours[1]} onChange={(e) => onChange({ hours: [Math.min(cfg.hours[0], Number(e.target.value) - 4), Number(e.target.value)] })}>
                {hours.slice(4).map((h) => (
                  <option key={h} value={h}>
                    {hh(h)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-xs text-[var(--ink-soft)]">Termine außerhalb erscheinen am Rand des Rasters.</p>
        </fieldset>
      )}
    </>
  );
}

/** Gruppen im Katalog „Baustein hinzufügen“ */
const CATALOG: { title: string; types: WidgetType[] }[] = [
  { title: 'Aufgaben und Termine', types: ['deadlines', 'tasks', 'blocked', 'ready', 'agenda', 'calendar', 'activity'] },
  { title: 'Statistiken und Diagramme', types: ['flow', 'distribution', 'trend', 'punctuality', 'workload', 'metric', 'time', 'timeStats'] },
  { title: 'Navigation', types: ['boards', 'links'] },
];
// Neue Typen, die hier noch fehlen, erscheinen trotzdem (unter „Weitere“)
const UNLISTED = WIDGET_TYPES.filter((t) => !CATALOG.some((g) => g.types.includes(t)));
if (UNLISTED.length) CATALOG.push({ title: 'Weitere', types: UNLISTED });

/** Baustein hinzufügen: Katalog mit kurzer Erklärung */
export function AddWidgetSheet({ open, count, onClose, onAdd, ctx = PERSONAL }: { open: boolean; count: number; onClose: () => void; onAdd: (w: Widget) => void; ctx?: DashCtx }) {
  const full = count >= MAX_WIDGETS;
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Baustein hinzufügen" side="center">
      <div className="flex flex-col gap-2 p-4">
        {full && <p className="text-sm text-amber-800 dark:text-amber-300">Höchstens {MAX_WIDGETS} Bausteine je Übersicht.</p>}
        {CATALOG.map((g) => (
          <section key={g.title} aria-labelledby={`cat-${g.title}`}>
            <h3 id={`cat-${g.title}`} className="mt-2 text-xs font-semibold tracking-wide text-[var(--ink-soft)] uppercase">
              {g.title}
            </h3>
            <ul className="flex flex-col divide-y divide-[var(--line)]">
              {g.types.filter((t) => WIDGET_TYPES.includes(t) && (ctx.scope !== 'board' || t !== 'boards')).map((t: WidgetType) => (
                <li key={t} className="flex items-center gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{WIDGET_INFO[t].title}</p>
                    <p className="text-xs text-[var(--ink-soft)]">{WIDGET_INFO[t].text}</p>
                  </div>
                  <Button
                    disabled={full}
                    aria-label={`${WIDGET_INFO[t].title} hinzufügen`}
                    onClick={() => {
                      onAdd({ id: newWidgetId(), type: t, size: WIDGET_INFO[t].size, title: null, filter: {}, config: defaultConfig(t) } as Widget);
                      onClose();
                    }}
                  >
                    <Plus size={15} aria-hidden /> Hinzufügen
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Sheet>
  );
}

/** Neue Übersicht aus einer Vorlage (nur Einstellungen, keine Daten) */
export function NewDashboardSheet({
  open,
  teams,
  onClose,
  onCreate,
}: {
  open: boolean;
  teams: { id: string; name: string }[];
  onClose: () => void;
  onCreate: (t: DashboardTemplate, title: string, teamId: string | null) => Promise<void>;
}) {
  const [t, setT] = useState<DashboardTemplate>('arbeitstag');
  const [title, setTitle] = useState('');
  const [teamId, setTeamId] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setT('arbeitstag');
      setTitle('');
      setTeamId('');
    }
  }, [open]);
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Neue Übersicht" side="center">
      <form
        className="flex flex-col gap-4 p-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            await onCreate(t, title.trim() || DASHBOARD_TEMPLATES[t].title, teamId || null);
            onClose();
          } finally {
            setBusy(false);
          }
        }}
      >
        {teams.length > 0 && (
          <label className="flex flex-col gap-1 text-sm" htmlFor="nd-for">
            <span className="font-semibold">Für</span>
            <select id="nd-for" className={inputCls} value={teamId} onChange={(e) => setTeamId(e.target.value)}>
              <option value="">Nur für mich</option>
              {teams.map((x) => (
                <option key={x.id} value={x.id}>
                  Team {x.name} (gemeinsam)
                </option>
              ))}
            </select>
            {teamId && <span className="text-xs text-[var(--ink-soft)]">Alle Teammitglieder sehen die Übersicht – jede Person mit den Zahlen aus ihren eigenen Boards.</span>}
          </label>
        )}
        <fieldset className={fieldset}>
          <legend className={legend}>Vorlage</legend>
          {(Object.keys(DASHBOARD_TEMPLATES) as DashboardTemplate[]).map((k) => (
            <label key={k} className={cx('flex min-h-14 items-start gap-3 rounded-lg border p-2.5', t === k ? 'border-accent bg-accent-soft/40' : 'border-[var(--line)]')}>
              <input type="radio" name="dash-tpl" className={cx(check, 'mt-0.5')} checked={t === k} onChange={() => setT(k)} />
              <span>
                <span className="block text-sm font-medium">{DASHBOARD_TEMPLATES[k].title}</span>
                <span className="block text-xs text-[var(--ink-soft)]">{DASHBOARD_TEMPLATES[k].text}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <label className="flex flex-col gap-1 text-sm" htmlFor="nd-title">
          <span className="font-semibold">Name</span>
          <input id="nd-title" className={inputCls} maxLength={80} placeholder={DASHBOARD_TEMPLATES[t].title} value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <p className="text-xs text-[var(--ink-soft)]">Vorlagen enthalten nur Einstellungen. Die Zahlen kommen immer aus deinen Boards.</p>
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            Anlegen
          </Button>
        </div>
      </form>
    </Sheet>
  );
}

/* ---------- Personen und eigene Felder ---------- */

type Options = { people: { id: string; name: string }[]; boards: { id: string; title: string; fields: CustomField[]; labels?: { id: string; name: string; color: LabelColor }[] }[] };

/** Personen aus gemeinsamen Boards und Felder der Boards im Umfang (nur mit Zugriff) */
function useDashOptions(boardIds: string[]) {
  const key = boardIds.slice().sort().join(',');
  return useQuery({ queryKey: ['dashboard-options', key], queryFn: () => api.get<Options>(`/api/dashboard-options?boardIds=${key}`), staleTime: 60_000 }).data;
}

/** Zuständig: alle, ich, niemand oder bestimmte Personen */
function PeopleField({ id, value, onChange }: { id: string; value: string[]; onChange: (v: string[]) => void }) {
  const specific = value.some((x) => x !== 'me' && x !== 'none');
  const [some, setSome] = useState(specific);
  const mode = some || specific ? 'some' : (value[0] ?? '');
  const opts = useDashOptions([]);
  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-1 text-sm" htmlFor={`${id}-who`}>
        <span className="font-semibold">Zuständig</span>
        <select
          id={`${id}-who`}
          className={inputCls}
          value={mode}
          onChange={(e) => {
            setSome(e.target.value === 'some');
            onChange(e.target.value === 'some' || !e.target.value ? [] : [e.target.value]);
          }}
        >
          <option value="">Alle</option>
          <option value="me">Mir zugewiesen</option>
          <option value="none">Ohne Zuständige</option>
          <option value="some">Bestimmte Personen …</option>
        </select>
      </label>
      {mode === 'some' && (
        <fieldset className="max-h-48 overflow-y-auto rounded-lg border border-[var(--line)] p-2">
          <legend className="sr-only">Personen auswählen</legend>
          {(opts?.people ?? []).map((p) => (
            <label key={p.id} className={row}>
              <input type="checkbox" className={check} checked={value.includes(p.id)} onChange={(e) => onChange(e.target.checked ? [...value.filter((x) => x !== 'me' && x !== 'none'), p.id] : value.filter((x) => x !== p.id))} />
              {p.name}
            </label>
          ))}
          <p className="mt-1 text-xs text-[var(--ink-soft)]">Karten mit mehreren Zuständigen zählen einmal.</p>
        </fieldset>
      )}
    </div>
  );
}

/**
 * 0.82: Kennzahl – Auswertung, Zuordnung je Board (nur Zahlenfelder gleicher Art/Einheit), Aufteilung, Ziel.
 * Gleichnamige Felder werden nicht automatisch verbunden.
 */
function MetricSettings({ id, cfg, filter, onChange }: { id: string; cfg: Extract<Widget, { type: 'metric' }>['config']; filter: DashFilter; onChange: (c: Partial<Extract<Widget, { type: 'metric' }>['config']>) => void }) {
  const { data: boards } = useBoards();
  const scopeIds = useMemo(() => {
    if (filter.boards.mode === 'selected') return filter.boards.ids;
    if (filter.boards.mode === 'team') return (boards ?? []).filter((b) => !b.archivedAt && b.teamId === (filter.boards as { teamId: string }).teamId).map((b) => b.id);
    return (boards ?? []).filter((b) => !b.archivedAt).map((b) => b.id).slice(0, 100);
  }, [boards, filter.boards]);
  const opts = useDashOptions(scopeIds);
  const countOnly = cfg.agg === 'count';
  const usable = (f: CustomField) => (countOnly ? aggregateOptions(f.type).includes('count') : isNumeric(f.type) && f.type !== 'rating') || (cfg.agg === 'avg' && f.type === 'rating');
  const withFields = (opts?.boards ?? []).filter((b) => b.fields.some(usable));
  const fieldById = new Map((opts?.boards ?? []).flatMap((b) => b.fields.map((x) => [x.id, x] as const)));
  const first = cfg.map.map((m) => fieldById.get(m.fieldId)).find(Boolean);
  return (
    <fieldset className={fieldset}>
      <legend className={legend}>Kennzahl</legend>
      <div className="flex flex-wrap gap-2">
        <label className="flex flex-col gap-1 text-sm" htmlFor={`${id}-agg`}>
          <span className="font-medium">Auswertung</span>
          <select id={`${id}-agg`} className={inputCls} value={cfg.agg} onChange={(e) => onChange({ agg: e.target.value as 'sum', map: [] })}>
            {(['sum', 'avg', 'min', 'max', 'count'] as const).map((k) => (
              <option key={k} value={k}>
                {k === 'count' ? 'Anzahl ausgefüllt' : AGGREGATE_LABEL[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm" htmlFor={`${id}-cards`}>
          <span className="font-medium">Karten</span>
          <select id={`${id}-cards`} className={inputCls} value={cfg.cards} onChange={(e) => onChange({ cards: e.target.value as 'all' })}>
            <option value="all">alle (ohne Archiv)</option>
            <option value="open">nur offene</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm" htmlFor={`${id}-grp`}>
          <span className="font-medium">Aufteilen nach</span>
          <select id={`${id}-grp`} className={inputCls} value={cfg.groupBy} onChange={(e) => onChange({ groupBy: e.target.value as 'none' })}>
            <option value="none">nicht aufteilen</option>
            <option value="column">Spalte</option>
            <option value="label">Label</option>
            <option value="assignee">Zuständige</option>
            <option value="month">Monat der Frist</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm" htmlFor={`${id}-target`}>
          <span className="font-medium">Ziel (optional)</span>
          <DecimalInput id={`${id}-target`} className={cx(inputCls, 'w-32')} value={cfg.target} min={-1e12} max={1e12} onChange={(target) => onChange({ target })} />
        </label>
        {cfg.target !== null && (
          <label className="flex flex-col gap-1 text-sm" htmlFor={`${id}-tmode`}>
            <span className="font-medium">Ziel bedeutet</span>
            <select id={`${id}-tmode`} className={inputCls} value={cfg.targetMode ?? 'neutral'} onChange={(e) => onChange({ targetMode: e.target.value as 'neutral' })}>
              <option value="neutral">nur Vergleich (neutral)</option>
              <option value="reach">erreichen – mehr ist gut</option>
              <option value="limit">Obergrenze – mehr ist schlecht</option>
            </select>
          </label>
        )}
      </div>
      <span className="text-sm font-medium">Feld je Board</span>
      {withFields.map((b) => {
        const m = cfg.map.find((x) => x.boardId === b.id);
        const list = b.fields.filter((f) => usable(f) && (!first || sameMeasure(first, f) || f.id === m?.fieldId));
        const chosen = new Set(m?.labelIds ?? []);
        const toggle = (labelId: string) => {
          if (!m) return;
          const next = chosen.has(labelId) ? [...chosen].filter((x) => x !== labelId) : [...chosen, labelId];
          const { labelIds: _l, ...base } = m;
          onChange({ map: cfg.map.map((x) => (x.boardId === b.id ? (next.length ? { ...base, labelIds: next } : base) : x)) });
        };
        return (
          <div key={b.id} className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="min-w-28 flex-1 break-words">{b.title}</span>
              <select
                aria-label={`Feld in ${b.title}`}
                className={cx(inputCls, 'w-auto min-w-40 flex-1')}
                value={m?.fieldId ?? ''}
                onChange={(e) => {
                  const rest = cfg.map.filter((x) => x.boardId !== b.id);
                  onChange({ map: e.target.value ? [...rest, { boardId: b.id, fieldId: e.target.value, ...(m?.labelIds ? { labelIds: m.labelIds } : {}) }] : rest });
                }}
              >
                <option value="">– nicht zugeordnet –</option>
                {list.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                    {fieldUnit(f) ? ` (${fieldUnit(f)})` : ''}
                  </option>
                ))}
              </select>
            </div>
            {/* 0.84: nur Karten mit diesen Labels (mindestens eines) */}
            {m && (b.labels?.length ?? 0) > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 text-xs" role="group" aria-label={`Nur Karten mit Label in ${b.title}`}>
                <span className="text-[var(--ink-soft)]">Nur mit Label:</span>
                {b.labels!.map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    aria-pressed={chosen.has(l.id)}
                    onClick={() => toggle(l.id)}
                    className={cx('min-h-11 rounded-full border px-3 py-1', chosen.has(l.id) ? 'border-transparent bg-accent text-on-accent' : 'border-[var(--line)] hover:bg-black/5 dark:hover:bg-white/10')}
                  >
                    {l.name || 'ohne Namen'}
                  </button>
                ))}
                {!chosen.size && <span className="text-[var(--ink-soft)]">(alle Karten)</span>}
              </div>
            )}
          </div>
        );
      })}
      {!withFields.length && <p className="text-xs text-[var(--ink-soft)]">Die Boards im Umfang haben keine passenden Felder.</p>}
      <p className="text-xs text-[var(--ink-soft)]">Nur Felder gleicher Art und Einheit bzw. Währung lassen sich zusammen auswerten. Karten aus Boards ohne Zuordnung zählen nicht mit.</p>
    </fieldset>
  );
}

const KIND_TEXT = { text: 'Text', number: 'Zahl', currency: 'Betrag', choice: 'Auswahl', date: 'Datum', checkbox: 'Ja/Nein', person: 'Person', approval: 'Freigabe' } as const;

/**
 * Bedingungen auf eigene Felder über mehrere Boards (F02): je Board wird das Feld ausdrücklich zugeordnet,
 * gleichnamige Felder werden nicht automatisch verbunden. Nur gleichartige Felder lassen sich kombinieren.
 */
function FieldConds({ id, f, ctx, onChange }: { id: string; f: DashFilter; ctx: DashCtx; onChange: (v: FieldCond[]) => void }) {
  const { data: boards } = useBoards();
  const scopeIds = useMemo(() => {
    const all = (boards ?? []).filter((b) => !b.archivedAt).map((b) => b.id);
    if (f.boards.mode === 'selected') return f.boards.ids;
    if (f.boards.mode === 'team') return (boards ?? []).filter((b) => !b.archivedAt && b.teamId === (f.boards as { teamId: string }).teamId).map((b) => b.id);
    return all.slice(0, 100);
  }, [boards, f.boards]);
  const opts = useDashOptions(scopeIds);
  const conds = f.fields ?? [];
  const withFields = (opts?.boards ?? []).filter((b) => b.fields.length);
  const fieldById = new Map((opts?.boards ?? []).flatMap((b) => b.fields.map((x) => [x.id, x] as const)));
  const upd = (i: number, c: FieldCond) => onChange(conds.map((x, j) => (j === i ? c : x)));
  return (
    <fieldset className={fieldset}>
      <legend className={legend}>Eigene Felder</legend>
      <p className="text-xs text-[var(--ink-soft)]">
        Gleichnamige Felder verschiedener Boards sind nicht automatisch dasselbe. Ordne je Board ausdrücklich zu; Karten aus Boards ohne Zuordnung zählen nicht mit.
        {ctx.scope === 'board' ? '' : ' Boards ohne eigene Felder erscheinen nicht.'}
      </p>
      {conds.map((c, i) => {
        const first = c.map.map((m) => fieldById.get(m.fieldId)).find(Boolean);
        const kind = first ? fieldKind(first.type) : null;
        const ops: FieldOp[] = kind ? FIELD_OPS_BY_KIND[kind] : ['is'];
        return (
          <div key={i} className="flex flex-col gap-2 rounded-lg border border-[var(--line)] p-2">
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex min-w-36 flex-1 flex-col gap-1 text-sm" htmlFor={`${id}-l${i}`}>
                <span className="font-medium">Bezeichnung</span>
                <input id={`${id}-l${i}`} className={inputCls} maxLength={60} value={c.label} onChange={(e) => upd(i, { ...c, label: e.target.value })} />
              </label>
              <Button variant="ghost" aria-label={`Bedingung „${c.label}“ entfernen`} onClick={() => onChange(conds.filter((_, j) => j !== i))}>
                <Trash2 size={15} aria-hidden />
              </Button>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">Zuordnung je Board{kind ? ` (${KIND_TEXT[kind]})` : ''}</span>
              {withFields.map((b) => {
                const m = c.map.find((x) => x.boardId === b.id);
                const usable = b.fields.filter((x) => !kind || fieldKind(x.type) === kind || x.id === m?.fieldId);
                const fld = m ? fieldById.get(m.fieldId) : undefined;
                return (
                  <div key={b.id} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="min-w-28 flex-1 break-words">{b.title}</span>
                    <select
                      aria-label={`Feld in ${b.title}`}
                      className={cx(inputCls, 'w-auto min-w-40 flex-1')}
                      value={m?.fieldId ?? ''}
                      onChange={(e) => {
                        const rest = c.map.filter((x) => x.boardId !== b.id);
                        upd(i, { ...c, map: e.target.value ? [...rest, { boardId: b.id, fieldId: e.target.value, optionId: null }] : rest });
                      }}
                    >
                      <option value="">– nicht zugeordnet –</option>
                      {usable.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                    </select>
                    {fld && fieldKind(fld.type) === 'choice' && c.op === 'is' && (
                      <select
                        aria-label={`Option in ${b.title}`}
                        className={cx(inputCls, 'w-auto min-w-32 flex-1')}
                        value={m?.optionId ?? ''}
                        onChange={(e) => upd(i, { ...c, map: c.map.map((x) => (x.boardId === b.id ? { ...x, optionId: e.target.value || null } : x)) })}
                      >
                        <option value="">Option wählen …</option>
                        {fld.options.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                );
              })}
              {!withFields.length && <p className="text-xs text-[var(--ink-soft)]">Die Boards im Umfang haben keine eigenen Felder.</p>}
            </div>
            {kind && (
              <div className="flex flex-wrap gap-2">
                <label className="flex flex-col gap-1 text-sm" htmlFor={`${id}-o${i}`}>
                  <span className="font-medium">Vergleich</span>
                  <select id={`${id}-o${i}`} className={inputCls} value={c.op} onChange={(e) => upd(i, { ...c, op: e.target.value as FieldOp })}>
                    {ops.map((o) => (
                      <option key={o} value={o}>
                        {FIELD_OP_TEXT[o]}
                      </option>
                    ))}
                  </select>
                </label>
                {kind !== 'choice' && kind !== 'person' && kind !== 'approval' && c.op !== 'empty' && c.op !== 'filled' && (
                  <label className="flex min-w-32 flex-1 flex-col gap-1 text-sm" htmlFor={`${id}-v${i}`}>
                    <span className="font-medium">Wert</span>
                    {kind === 'checkbox' ? (
                      <select id={`${id}-v${i}`} className={inputCls} value={c.value === true ? '1' : '0'} onChange={(e) => upd(i, { ...c, value: e.target.value === '1' })}>
                        <option value="1">ja</option>
                        <option value="0">nein</option>
                      </select>
                    ) : (
                      <input
                        id={`${id}-v${i}`}
                        className={inputCls}
                        type={kind === 'date' ? 'date' : kind === 'number' || kind === 'currency' ? 'number' : 'text'}
                        step="any"
                        value={c.value === null || c.value === undefined ? '' : String(c.value)}
                        onChange={(e) => upd(i, { ...c, value: kind === 'number' || kind === 'currency' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value })}
                      />
                    )}
                  </label>
                )}
              </div>
            )}
          </div>
        );
      })}
      {conds.length < 5 && (
        <Button className="self-start" onClick={() => onChange([...conds, { label: 'Feld', op: 'is', value: null, map: [] }])}>
          <Plus size={15} aria-hidden /> Bedingung
        </Button>
      )}
    </fieldset>
  );
}
